import { useState } from 'react';

import { EditorState, convertToRaw } from 'draft-js';
import { observer } from 'mobx-react-lite';

import { Button } from '@components/button';
import { Select } from '@components/select';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import {
  convertPlaceholders,
  placeholdersToBrackets,
} from '@components/text-editor/plugin-placeholder';
import { draftToHtml, isEditorEmpty, makeEditorState } from '@utils/draftjs';

import { Loading } from 'components/Loading';
import ShowMore from 'components/ShowMore';
import { Modal, useModal } from 'features/modals';
import { useStageEntryJourneyPrompt } from 'integrations/journeys/job-triggers/use-stage-entry-journey';
import { useStore } from 'models/Provider';

import RejectForm from './RejectForm';
import { RejectName } from './styles';

function RejectApplicantsModal({ applicants, applicant, closeModal, onSuccess }) {
  const { current, t } = useStore();
  const [submitting, setSubmitting] = useState(false);
  const [method, setMethod] = useState(null);
  const [selected, setSelected] = useState({
    message: EditorState.createEmpty(),
    id: -1,
  });
  const templates = current.rejectionTemplates;
  const { setModalAfterClose } = useModal();
  const { promptOutcomeJourney } = useStageEntryJourneyPrompt(
    (applicant ?? applicants?.[0])?.job?.id ?? 0,
  );

  const rejectionTypes = [
    {
      value: 'reject',
      label: t('rejectmodal.types.reject'),
      helpText: t('rejectmodal.types.rejecthelp'),
    },
    {
      value: 'rejectAndSendEmail',
      label: t('rejectmodal.types.rejectsend'),
      helpText: t('rejectmodal.types.rejectsendhelp'),
    },
    {
      value: 'markRejectedAndNotified',
      label: t('rejectmodal.types.rejectmark'),
      helpText: t('rejectmodal.types.rejectmarkhelp'),
    },
  ];

  function onTemplateSelect(id) {
    const templates = current.rejectionTemplates;
    const select = templates.find((t) => t.id === Number(id));
    if (select) {
      const { contentHtml, contentTxt } = select;
      const editorState = makeEditorState(contentHtml || contentTxt);

      setSelected({
        message: convertPlaceholders(editorState, t),
        id,
      });
    }
  }

  function setEditorState(editorState) {
    setSelected((old) => ({
      ...old,
      message: convertPlaceholders(editorState, t),
    }));
  }

  async function onSubmit() {
    const { message } = selected;
    const job = applicant ? applicant.job : applicants[0].job;

    // dataForMethod() sorts on isRejected / isNotifiedOfRejection, and the
    // reject actions below flip both on success — so the acted-on list has to
    // be read before the await, not after. The "wont" list (already rejected /
    // already notified) is excluded from the action and must not be prompted.
    const { will } = dataForMethod();
    const rejectedIds = applicant ? [applicant.id] : will.map((rejected) => rejected.id);

    let success = true;
    setSubmitting(true);

    switch (method) {
      case 'reject':
        if (applicant) {
          success = await applicant.reject(false);
        } else {
          success = await job.bulkRejectApplicants(applicants, false);
        }
        break;
      case 'markRejectedAndNotified':
        if (applicant) {
          success = await applicant.reject(true);
        } else {
          success = await job.bulkRejectApplicants(applicants, true);
        }
        break;
      case 'rejectAndSendEmail':
        const rawData = convertToRaw(message.getCurrentContent());
        const converted = placeholdersToBrackets(rawData);

        const emailData = {
          message: draftToHtml(converted),
        };
        if (applicant) {
          emailData.applicant = applicant.id;
          success = await applicant.reject(true, emailData);
        } else {
          success = await job.bulkRejectApplicants(applicants, true, emailData);
        }
        break;
      default:
        return;
    }

    if (success) {
      // Outcome wins: a stage prompt chained on this modal must stand down
      // when the rejection succeeded — the rejected-outcome prompt below is
      // the more specific event.
      setModalAfterClose('REJECT_APPLICANTS', null);
      closeModal();
      onSuccess?.(method);
      promptOutcomeJourney(rejectedIds, 'rejected');
    } else {
      setSubmitting(false);
    }
  }

  function dataForMethod() {
    let will = [];
    let wont = [];

    if (applicants) {
      switch (method) {
        case 'reject':
          applicants.forEach((applicant) => {
            if (applicant.isRejected) {
              wont.push(applicant);
            } else {
              will.push(applicant);
            }
          });
          break;
        case 'markRejectedAndNotified':
          applicants.forEach((applicant) => {
            if (applicant.isNotifiedOfRejection) {
              wont.push(applicant);
            } else {
              will.push(applicant);
            }
          });
          break;
        case 'rejectAndSendEmail':
          applicants.forEach((applicant) => {
            if (applicant.isNotifiedOfRejection) {
              wont.push(applicant);
            } else {
              will.push(applicant);
            }
          });
          break;
        default:
          break;
      }
    }
    return { will, wont };
  }

  let notRejected = [];
  let notNotified = [];
  let rejected = [];
  let rejectedAndNotified = [];
  if (applicants && method !== null) {
    applicants.forEach((a) => {
      if (a.isNotifiedOfRejection) {
        rejectedAndNotified.push(a);
      } else if (a.isRejected) {
        rejected.push(a);
        notNotified.push(a);
      } else {
        notRejected.push(a);
      }
    });
  }

  const { will, wont } = dataForMethod();

  const disableButtons =
    method === '' ||
    submitting ||
    (method === 'rejectAndSendEmail' && isEditorEmpty(selected.message));
  return (
    <Modal>
      <Modal.Header text={t('rejectmodal.title')} />
      {submitting && <Loading />}

      {applicant && (
        <Text
          text={
            <>
              {t('rejectmodal.confirm.single')} <strong>{applicant.name}</strong>?
            </>
          }
        />
      )}

      {applicants && (
        <>
          {method === null && (
            <Stack space={3}>
              <Text text={t('rejectmodal.confirm.multi')} />
              <ShowMore
                max={applicants.length}
                count={applicants.length > 15 ? 10 : applicants.length}
                inc={1000}
                render={({ count }) => (
                  <div>
                    {applicants.slice(0, count).map((a, i) => (
                      <RejectName key={i}>
                        <span>{a.name}</span>
                      </RejectName>
                    ))}
                  </div>
                )}
              />
            </Stack>
          )}

          {method !== null && will.length > 0 && (
            <Stack space={3}>
              {method === 'reject' && <Text text={t('rejectmodal.confirmed.reject')} />}
              {method === 'rejectAndSendEmail' && (
                <Text text={t('rejectmodal.confirmed.rejectsend')} />
              )}
              {method === 'markRejectedAndNotified' && (
                <Text text={t('rejectmodal.confirmed.rejectmark')} />
              )}
              <ShowMore
                key={will.length}
                max={will.length}
                count={will.length > 15 ? 10 : will.length}
                inc={100}
                render={({ count }) => (
                  <div>
                    {will.slice(0, count).map((a, i) => (
                      <RejectName key={i}>
                        <span>{a.name}</span>
                      </RejectName>
                    ))}
                  </div>
                )}
              />
            </Stack>
          )}

          {method !== null && wont.length > 0 && (
            <Stack space={3}>
              <Text text={t('rejectmodal.excluded')} />
              <ShowMore
                key={will.length}
                max={wont.length}
                count={wont.length > 15 ? 10 : wont.length}
                inc={100}
                render={({ count }) => (
                  <div>
                    {wont.slice(0, count).map((a, i) => (
                      <div key={i}>
                        <RejectName>
                          <span>{a.name}</span>
                          <span>
                            {a.isNotifiedOfRejection
                              ? t('rejectmodal.already.notified')
                              : t('rejectmodal.already.rejected')}
                          </span>
                        </RejectName>
                      </div>
                    ))}
                  </div>
                )}
              />
            </Stack>
          )}
        </>
      )}

      <Separator />

      <Select
        isSearchable={false}
        isClearable={false}
        options={rejectionTypes}
        label={t('rejectmodal.method.title')}
        placeholder={t('rejectmodal.method.drop.title')}
        onChange={(method) => setMethod(method)}
      />

      <RejectForm
        disabled={submitting}
        isOpen={method === 'rejectAndSendEmail'}
        templates={templates}
        selected={selected}
        onSelect={onTemplateSelect}
        setEditorState={setEditorState}
      />
      <Modal.Actions>
        <Button
          variant="secondary"
          isDisabled={submitting}
          onClick={closeModal}
          text={t('rejectmodal.button.cancel')}
        />
        <Button
          variant="primary-red"
          isDisabled={disableButtons || !method}
          onClick={onSubmit}
          text={t('rejectmodal.button.submit')}
        />
      </Modal.Actions>
    </Modal>
  );
}

export default observer(RejectApplicantsModal);
